import cn from "classnames";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";

import { type EntityType } from "features/Library/Flows/Designer/types";
import AssessmentReport from "./sections/Assessments/AssessmentReport";
import { ContentReportDrilldown } from "./sections/Drilldown/ContentReportDrilldown";
import EmailReport from "./sections/Emails/EmailReport";
import FlowsReport from "./sections/Flows/FlowsReport";
import LinksOverviewReport from "./sections/Links/LinksOverviewReport";
import MessageOverviewReport from "./sections/Messages/MessageOverviewReport";
import PdfReport from "./sections/PDFs/PdfReport";
import SurveyReport from "./sections/Surveys/SurveyReport";
import VideoReport from "./sections/Videos/VideoReport";
import EventsReport from "./sections/Events/EventsReport";
import PhishingCampaignsReport from "./sections/PhishingCampaigns/PhishingCampaignsReport";
import {
  canUseChildAccount,
  normalizeStatusFilter,
  type PerformanceWithAccountFilter,
} from "features/Library/Common/utils/performanceUtils";
import { useHasPermissions } from "features/Application/hooks";
import { RolePermissions, RouteNames } from "enums";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { FeatureFlags } from "featureFlags";

import styles from "./contentOverviewReports.module.scss";

export type ContentReportSelection = {
  id: number;
  name: string;
  type: EntityType;
};

type Props = {
  filter: PerformanceWithAccountFilter;
  selectIsReportEnabled: boolean;
  usesDropdownMenuNavigation?: boolean;
  childAccountId?: number;
  hasCreateAccountsAddOn?: boolean;
  registerBreadcrumb: (crumb: { text: React.ReactNode; action?: () => void }, overrideAndReset?: boolean) => void;
  resetBreadcrumbs: () => void;
  goBackNLevels: (n: number) => void;
  accountFilterShouldShow: boolean;
};

export const ContentReportsView: FC<Props> = ({
  filter,
  selectIsReportEnabled,
  usesDropdownMenuNavigation,
  childAccountId,
  hasCreateAccountsAddOn,
  registerBreadcrumb,
  resetBreadcrumbs,
  goBackNLevels,
  accountFilterShouldShow,
}) => {
  const [selectedContent, setSelectedContent] = useState<ContentReportSelection>();
  const location = useLocation();
  const isLinksAssetTypeEnabled = useFeatureFlag(FeatureFlags.LinkAssetType);
  const isMessagesReportEnabled = useFeatureFlag(FeatureFlags.MessagesReport);
  const hasManagePermission = useHasPermissions([RolePermissions.CampaignsManage]);

  const isChildAccountContext = canUseChildAccount(childAccountId, hasCreateAccountsAddOn);

  useEffect(() => {
    setSelectedContent(undefined);
  }, [location.pathname]);

  const handleReset = useCallback(() => {
    setSelectedContent(undefined);
    resetBreadcrumbs();
  }, [resetBreadcrumbs]);

  const commonReportFilter = useMemo(
    () => ({
      dateTo: filter.dateTo,
      dateFrom: filter.dateFrom,
      accounts: filter.accounts,
      includeMyData: filter.includeMyData,
      isDistinct: filter.isDistinct ?? false,
      type: filter.type,
    }),
    [filter.dateTo, filter.dateFrom, filter.accounts, filter.includeMyData, filter.isDistinct, filter.type],
  );

  const flowReportFilter = useMemo(() => {
    const normalizedStatus = filter.status ? normalizeStatusFilter(filter.status) : undefined;
    return {
      dateTo: filter.dateTo,
      dateFrom: filter.dateFrom,
      accounts: filter.accounts,
      includeMyData: filter.includeMyData,
      isDistinct: filter.isDistinct ?? false,
      type: filter.type,
      priority: filter.priority,
      status: normalizedStatus,
    };
  }, [
    filter.dateTo,
    filter.dateFrom,
    filter.accounts,
    filter.includeMyData,
    filter.isDistinct,
    filter.type,
    filter.priority,
    filter.status,
  ]);

  const videoReportFilter = useMemo(
    () => ({
      dateTo: filter.dateTo,
      dateFrom: filter.dateFrom,
      accounts: filter.accounts,
      includeMyData: filter.includeMyData,
      isDistinct: filter.isDistinct,
      videoType: filter.videoType ?? "All",
      type: filter.type,
    }),
    [
      filter.dateTo,
      filter.dateFrom,
      filter.accounts,
      filter.includeMyData,
      filter.isDistinct,
      filter.videoType,
      filter.type,
    ],
  );

  if (selectedContent) {
    return (
      <div className={cn("performanceRoot", { [styles.removePadding]: !selectIsReportEnabled })}>
        <ContentReportDrilldown
          {...selectedContent}
          filter={filter}
          registerBreadcrumb={registerBreadcrumb}
          onReset={handleReset}
          goBackNLevels={goBackNLevels}
          includeAccounts={accountFilterShouldShow && (filter.accounts ?? []).length > 0 && !isChildAccountContext}
          childAccountId={childAccountId}
          hasCreateAccountsAddOn={hasCreateAccountsAddOn}
        />
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="flows"
        element={
          <FlowsReport
            filter={flowReportFilter}
            setSelectedContent={setSelectedContent}
            includeAccounts={(filter.accounts ?? []).length > 0}
            childAccountId={childAccountId}
            hasCreateAccountsAddOn={hasCreateAccountsAddOn}
          />
        }
      />
      <Route path="pdfs" element={<PdfReport filter={commonReportFilter} setSelectedContent={setSelectedContent} />} />
      <Route
        path="videos"
        element={<VideoReport filter={videoReportFilter} setSelectedContent={setSelectedContent} />}
      />
      <Route
        path="assessments"
        element={<AssessmentReport filter={commonReportFilter} setSelectedContent={setSelectedContent} />}
      />
      <Route
        path="surveys"
        element={<SurveyReport filter={commonReportFilter} setSelectedContent={setSelectedContent} />}
      />
      <Route
        path="emails"
        // Ignoring "isDistinct" prop for now
        // @ts-ignore
        element={<EmailReport filter={commonReportFilter} setSelectedContent={setSelectedContent} />}
      />
      <Route
        path="events"
        element={
          <EventsReport
            filter={commonReportFilter}
            setSelectedContent={setSelectedContent}
            includeAccounts={(filter.accounts ?? []).length > 0}
            childAccountId={childAccountId}
            hasCreateAccountsAddOn={hasCreateAccountsAddOn}
          />
        }
      />
      {isLinksAssetTypeEnabled && (
        <Route
          path="links"
          element={<LinksOverviewReport filter={commonReportFilter} setSelectedContent={setSelectedContent} />}
        />
      )}
      {isMessagesReportEnabled && (
        <Route
          path="messages"
          element={<MessageOverviewReport filter={commonReportFilter} setSelectedContent={setSelectedContent} />}
        />
      )}
      {hasManagePermission && (
        <Route
          path="campaigns"
          element={<PhishingCampaignsReport filter={commonReportFilter} setSelectedContent={setSelectedContent} />}
        />
      )}
      <Route
        path="*"
        element={
          <Navigate to={usesDropdownMenuNavigation ? "../flows" : `/${RouteNames.reporting}/content/flows`} replace />
        }
      />
    </Routes>
  );
};
